import { btnDemo, btnSales } from '../components/cta';
import { architecture, escalation, smartRouting, traceCard } from '../components/diagrams';
import { integrationsSection } from '../components/integrations';
import { MOCKS } from '../components/mockups';
import { videoSection } from '../components/platform-video';
import { eyebrow, mesh } from '../components/primitives';
import { ctaBand, howWeWork, sigBand } from '../components/sections';
import { INCEPTION_NOTE, ROUTING_HEAD } from './shared-content';

/* ============================ PLATFORM ============================ */
const AI_HELPS = [
  'extract',
  'classify',
  'organize',
  'summarize',
  'draft',
  'identify missing information',
  'route work',
  'prepare recommendations',
  'automate repetitive actions',
];
function capRow(id: string, n: number, h: string, sub: string, body: string, mock: string, extra = ''): string {
  return `
  <section class="s ${n % 2 ? '' : 'alt'}" id="${id}" aria-labelledby="${id}-h">
    <div class="wrap mrow ${n % 2 ? '' : 'flip'}">
      <div class="mtext rv">${eyebrow(`Capability 0${n - 4}`)}<h2 id="${id}-h" class="h-cap">${h}</h2><p class="sub">${sub}</p>${body}${extra}</div>
      <div class="rv" style="--d:100ms">${mock}</div>
    </div>
  </section>`;
}
export function pagePlatform(): string {
  return `
  <section class="hero center" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap rv">
      ${eyebrow('Platform')}
      <h1 id="h1" style="margin:22px auto 24px;max-width:900px">The right intelligence for <span class="grad">the right task.</span></h1>
      <p class="lede">ReTax360 is an enterprise AI orchestration and workflow platform purpose-built for property tax operations. It brings together the four foundations of the operation: clients, communication, data and documents.</p>
      <div class="ctas">${btnDemo()} ${btnSales()}</div>
    </div>
  </section>

  ${videoSection()}

  <section class="s" id="architecture" aria-labelledby="arch-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Platform architecture')}<h2 id="arch-h">Three layers. One governed system.</h2><p>Your workflow on top, configured around your organization. Intelligence and orchestration in the middle. One foundation underneath every workflow.</p></div>
      ${architecture()}
    </div>
  </section>

  ${sigBand('Orchestration is the difference between using AI and governing it.', { dark: true, grad: 'governing it.' })}

  <section class="s" id="orchestration" aria-labelledby="orch-h">
    <div class="wrap">
      ${ROUTING_HEAD('orch-h')}
      <div class="light-diagram">${smartRouting()}</div>
      ${INCEPTION_NOTE}
    </div>
  </section>

  <section class="s dark" id="escalation" aria-labelledby="esc-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Confidence-based escalation')}<h2 id="esc-h">Confidence-based escalation.</h2>
        <p>When ReTax360 encounters missing information, conflicting information, low-confidence AI results, business-rule exceptions, unusual workflow conditions or professional decisions requiring judgment, the task can be escalated to the appropriate human reviewer.</p></div>
      ${escalation()}
    </div>
  </section>

  ${capRow(
    'intake',
    5,
    'Intelligent Intake',
    'Turn incoming information into structured work.',
    '<p>ReTax360 helps organize incoming documents, emails and structured data, identify the appropriate client, property or case, identify missing information and route work into the appropriate workflow.</p>',
    MOCKS.intake(),
    `<p class="colh" style="margin-top:22px">More than document uploading. The platform keeps asking:</p><ul class="bullets"><li>What do we have?</li><li>What is missing?</li><li>Where can it be obtained?</li><li>Who or what should handle it next?</li></ul>`,
  )}
  ${capRow(
    'document-intelligence',
    6,
    'Document Intelligence',
    'Turn unstructured documents into structured workflow data.',
    '<p>ReTax360 helps extract information from property tax documents, classify documents, associate them with the appropriate client, property or case, and route the information into the relevant workflow. Where confidence or business rules require it, a professional can review the information before it becomes part of the official record.</p>',
    MOCKS.docs(),
  )}
  ${capRow(
    'settlement',
    7,
    'Settlement Intelligence',
    'Bring settlement activity into the same operating environment as the rest of the appeal.',
    '<p>Organize offers, counteroffers, supporting information, settlement status, savings, case history and related documents, to maintain a consistent record of settlement activity across cases and portfolios.</p><p class="note">AI does not accept settlements or make final settlement decisions. Final decisions remain with the responsible professional.</p>',
    MOCKS.settlement(),
  )}
  ${capRow(
    'client-portal',
    8,
    'Client Portal &amp; Portfolio Experience',
    'Give clients controlled visibility into the work that matters to them.',
    '<p>Bring case status, documents, deadlines, communication, reporting and portfolio information into a shared experience without requiring internal teams to manually answer every routine status request.</p>',
    MOCKS.portal(),
  )}
  ${capRow(
    'billing',
    9,
    'Billing &amp; Financials',
    'Financial workflow alongside the case, not in a separate system.',
    '<ul class="bullets"><li>Case financials</li><li>Invoicing</li><li>Fee tracking</li><li>Payment status</li><li>Savings calculations</li><li>Financial workflow</li></ul>',
    MOCKS.billing(),
  )}
  ${capRow(
    'reporting',
    10,
    'Reporting &amp; Portfolio Intelligence',
    'Turn operational activity into portfolio-level visibility.',
    '<p>Give practice leaders and client teams clearer visibility across Organization → Office → Market / Jurisdiction → Client → Property → Parcel → Case: case activity, deadlines, status, savings, workload, portfolio activity and reporting.</p>',
    MOCKS.reporting(),
  )}

  ${integrationsSection()}

  <section class="s dark" id="observability" aria-labelledby="obs-h">
    <div class="wrap grid2 obs">
      <div class="rv">
        ${eyebrow('AI observability')}
        <h2 id="obs-h" style="margin:16px 0">Intelligence you can inspect.</h2>
        <p class="lede" style="color:var(--on-dark-muted)">Enterprise AI should not operate as an unexplained black box. ReTax360 is designed so organizations can understand how work moved through the workflow.</p>
        <ol class="obs-flow" aria-label="Workflow trace sequence">${['Task received', 'Route selected', 'Model / rule / system / person', 'Action', 'Output', 'Confidence / verification', 'Human action if required', 'Final workflow state'].map((t) => `<li>${t}</li>`).join('')}</ol>
      </div>
      <div class="rv" style="--d:100ms">${traceCard()}</div>
    </div>
  </section>

  ${sigBand("The goal isn't to use more AI. The goal is to use the right intelligence at the right moment.", { grad: 'the right intelligence at the right moment.' })}

  <section class="s alt" id="human-expertise" aria-labelledby="he-h">
    <div class="wrap grid2" style="align-items:center;gap:56px">
      <div class="rv">${eyebrow('Human expertise')}<h2 id="he-h" style="margin:16px 0">AI prepares. Rules govern. Experts decide.</h2><p class="lede">Human professionals remain responsible for decisions requiring professional judgment. ReTax360 never positions AI as a replacement for the professional.</p></div>
      <div class="card rv"><div class="colh">AI may help</div><div class="tagrow">${AI_HELPS.map((t) => `<span class="tag">${t}</span>`).join('')}</div></div>
    </div>
  </section>

  ${howWeWork({ full: true, id: 'how-we-work' })}
  <section class="s tight-top" aria-labelledby="pilot-h">
    <div class="wrap"><div class="head rv"><h2 id="pilot-h" class="h-cap">The pilot path.</h2></div>${pilotPath()}</div>
  </section>
  ${ctaBand()}`;
}
const pilotPath = (): string =>
  `<ol class="pilot rv" aria-label="Pilot path">${['Discover', 'Configure', 'Pilot', 'Measure', 'Roll out', 'Expand'].map((t) => `<li>${t}</li>`).join('')}</ol>`;
